import { useEffect, useState, useCallback } from "react";
import api, { formatApiError, formatMoney, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Boxes, Plus, Trash2, CheckCircle2, XCircle, Image as ImageIcon, X, ShoppingCart, ClipboardList } from "lucide-react";

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function Inventario() {
  const [tab, setTab] = useState("stock");
  return (
    <div className="space-y-6" data-testid="inventario-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Almacén</div>
        <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
          <Boxes className="w-8 h-8 text-blue-700" /> Inventario
        </h1>
        <p className="text-zinc-500 mt-2 text-sm">
          Gestión de stock, presupuestos y compras a proveedores. Fase A (MVP).
        </p>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="stock" data-testid="tab-stock"><Boxes className="w-4 h-4 mr-2" />Stock</TabsTrigger>
          <TabsTrigger value="presupuestos" data-testid="tab-presupuestos"><ClipboardList className="w-4 h-4 mr-2" />Presupuestos</TabsTrigger>
          <TabsTrigger value="compras" data-testid="tab-compras"><ShoppingCart className="w-4 h-4 mr-2" />Compras</TabsTrigger>
        </TabsList>
        <TabsContent value="stock" className="mt-6"><StockPanel /></TabsContent>
        <TabsContent value="presupuestos" className="mt-6"><PresupuestosPanel onChange={() => setTab("compras")} /></TabsContent>
        <TabsContent value="compras" className="mt-6"><ComprasPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

// ---------- Panel STOCK ----------
function StockPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ nombre: "", tamano: "", color: "", unidad: "unidad", stock_inicial: 0, stock_minimo: 0, notas: "" });

  const load = async () => {
    try { const { data } = await api.get("/inventario/productos"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.nombre.trim()) return toast.error("Nombre requerido");
    try {
      await api.post("/inventario/productos", form);
      toast.success("Producto agregado");
      setForm({ nombre: "", tamano: "", color: "", unidad: "unidad", stock_inicial: 0, stock_minimo: 0, notas: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const bulkCreatePlanchas = async () => {
    const planchas = [
      { nombre: "Plancha Imperial", tamano: "Imperial" },
      { nombre: "Plancha Semimatrimonial", tamano: "Semimatrimonial" },
      { nombre: "Plancha Queen", tamano: "Queen" },
      { nombre: "Plancha King", tamano: "King" },
      { nombre: "Plancha Especial", tamano: "Especial" },
      { nombre: "Baúl pequeño", tamano: "Pequeño" },
      { nombre: "Baúl grande", tamano: "Grande" },
    ];
    if (!window.confirm(`¿Crear ${planchas.length} planchas / baúles comunes? Cada uno con stock 0.`)) return;
    try {
      let creados = 0;
      for (const p of planchas) {
        const dup = items.some((x) => x.nombre === p.nombre && (x.tamano || "") === p.tamano);
        if (dup) continue;
        await api.post("/inventario/productos", { ...p, unidad: "unidad", stock_inicial: 0, stock_minimo: 1, color: "", notas: "" });
        creados++;
      }
      toast.success(`${creados} planchas/baúles creados`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar producto de inventario?")) return;
    try { await api.delete(`/inventario/productos/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const updateStock = async (id, valor) => {
    try { await api.patch(`/inventario/productos/${id}`, { stock_actual: Number(valor) });
      setItems((prev) => prev.map((x) => x.id === id ? { ...x, stock_actual: Number(valor) } : x));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4 border-zinc-200 rounded-md">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-3 flex items-center justify-between gap-2">
          <span className="flex items-center gap-2"><Plus className="w-3 h-3" /> Agregar producto al inventario</span>
          <Button
            type="button" size="sm" variant="outline"
            onClick={bulkCreatePlanchas}
            className="text-[11px] border-blue-300 text-blue-800 hover:bg-blue-50 h-7"
            data-testid="inv-bulk-planchas"
          >
            + Planchas &amp; Baúles comunes
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-7 gap-2 items-end">
          <div><Label className="text-xs">Nombre *</Label><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Plancha, tela..." data-testid="inv-nombre" /></div>
          <div><Label className="text-xs">Tamaño</Label><Input value={form.tamano} onChange={(e) => setForm({ ...form, tamano: e.target.value })} placeholder="King, XL..." data-testid="inv-tamano" /></div>
          <div><Label className="text-xs">Color</Label><Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} data-testid="inv-color" /></div>
          <div><Label className="text-xs">Unidad</Label><Input value={form.unidad} onChange={(e) => setForm({ ...form, unidad: e.target.value })} data-testid="inv-unidad" /></div>
          <div><Label className="text-xs">Stock inicial</Label><Input type="number" step="0.01" value={form.stock_inicial} onChange={(e) => setForm({ ...form, stock_inicial: Number(e.target.value) })} data-testid="inv-stock-ini" /></div>
          <div><Label className="text-xs">Stock mínimo</Label><Input type="number" step="0.01" value={form.stock_minimo} onChange={(e) => setForm({ ...form, stock_minimo: Number(e.target.value) })} data-testid="inv-stock-min" /></div>
          <Button onClick={save} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="inv-save">Agregar</Button>
        </div>
      </Card>

      <Card className="border-zinc-200 rounded-md overflow-hidden">
        <div className="p-4 border-b border-zinc-100 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Stock ({items.length})</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr className="text-left">
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Producto</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Tamaño</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Color</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Unidad</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold text-right">Stock actual</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold text-right">Mínimo</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {items.length === 0 && <tr><td colSpan={7} className="text-center text-zinc-400 py-10">Sin productos.</td></tr>}
              {items.map((p) => {
                const bajo = p.stock_actual <= (p.stock_minimo || 0) && (p.stock_minimo || 0) > 0;
                return (
                  <tr key={p.id} className={bajo ? "bg-red-50 hover:bg-red-100" : "hover:bg-zinc-50"} data-testid={`inv-row-${p.id}`}>
                    <td className="px-3 py-2 font-semibold">{p.nombre}</td>
                    <td className="px-3 py-2 text-xs">{p.tamano || "—"}</td>
                    <td className="px-3 py-2 text-xs">{p.color || "—"}</td>
                    <td className="px-3 py-2 text-xs">{p.unidad}</td>
                    <td className="px-3 py-2 text-right">
                      <Input type="number" step="0.01" value={p.stock_actual}
                        onChange={(e) => setItems((prev) => prev.map((x) => x.id === p.id ? { ...x, stock_actual: Number(e.target.value) } : x))}
                        onBlur={(e) => updateStock(p.id, e.target.value)}
                        className={`w-20 text-right ${bajo ? "border-red-400 bg-red-50 font-bold text-red-700" : ""}`}
                        data-testid={`inv-stock-${p.id}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-right text-xs">{p.stock_minimo || 0}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => del(p.id)} className="text-red-600 text-xs hover:underline inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ---------- Panel PRESUPUESTOS ----------
function PresupuestosPanel({ onChange }) {
  const [items, setItems] = useState([]);
  const [productos, setProductos] = useState([]);
  const [form, setForm] = useState({ proveedor: "", fecha: todayIso(), items: [{ descripcion: "", cantidad: 1, precio_unitario: 0, producto_id: "" }], notas: "" });

  const load = async () => {
    try {
      const [p, prod] = await Promise.all([
        api.get("/inventario/presupuestos"),
        api.get("/inventario/productos"),
      ]);
      setItems(p.data); setProductos(prod.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const addItem = () => setForm({ ...form, items: [...form.items, { descripcion: "", cantidad: 1, precio_unitario: 0, producto_id: "" }] });
  const rmItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const updItem = (idx, k, v) => setForm({ ...form, items: form.items.map((x, i) => i === idx ? { ...x, [k]: v } : x) });
  const total = form.items.reduce((a, it) => a + Number(it.cantidad || 0) * Number(it.precio_unitario || 0), 0);

  const save = async () => {
    const valid = form.items.every((it) => it.descripcion.trim() && Number(it.cantidad) > 0);
    if (!valid) return toast.error("Todos los items requieren descripción y cantidad > 0");
    try {
      await api.post("/inventario/presupuestos", form);
      toast.success("Presupuesto creado");
      setForm({ proveedor: "", fecha: todayIso(), items: [{ descripcion: "", cantidad: 1, precio_unitario: 0, producto_id: "" }], notas: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cambiarEstado = async (id, estado) => {
    try { await api.patch(`/inventario/presupuestos/${id}/estado`, { estado }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar presupuesto?")) return;
    try { await api.delete(`/inventario/presupuestos/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const badgeCls = (e) => ({
    pendiente: "bg-yellow-100 text-yellow-800",
    aprobado: "bg-emerald-100 text-emerald-800",
    rechazado: "bg-red-100 text-red-800",
    comprado: "bg-blue-100 text-blue-800",
  }[e] || "bg-zinc-100 text-zinc-700");

  return (
    <div className="space-y-4">
      <Card className="p-5 border-zinc-200 rounded-md">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-3 flex items-center gap-2">
          <Plus className="w-3 h-3" /> Nuevo presupuesto
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
          <div><Label className="text-xs">Proveedor</Label><Input value={form.proveedor} onChange={(e) => setForm({ ...form, proveedor: e.target.value })} data-testid="pres-proveedor" /></div>
          <div><Label className="text-xs">Fecha</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="pres-fecha" /></div>
          <div><Label className="text-xs">Notas</Label><Input value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} data-testid="pres-notas" /></div>
        </div>
        {form.items.map((it, i) => (
          <div key={i} className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-2 items-end p-2 bg-zinc-50 rounded">
            <div className="md:col-span-2"><Label className="text-xs">Descripción *</Label><Input value={it.descripcion} onChange={(e) => updItem(i, "descripcion", e.target.value)} data-testid={`pres-desc-${i}`} /></div>
            <div>
              <Label className="text-xs">Producto (stock)</Label>
              <Select value={it.producto_id || "__none__"} onValueChange={(v) => updItem(i, "producto_id", v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid={`pres-prod-${i}`}><SelectValue placeholder="(opcional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Ninguno —</SelectItem>
                  {productos.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre} {p.tamano ? `· ${p.tamano}` : ""} {p.color ? `· ${p.color}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Cantidad</Label><Input type="number" step="0.01" value={it.cantidad} onChange={(e) => updItem(i, "cantidad", Number(e.target.value))} data-testid={`pres-cant-${i}`} /></div>
            <div><Label className="text-xs">Precio</Label><Input type="number" step="0.01" value={it.precio_unitario} onChange={(e) => updItem(i, "precio_unitario", Number(e.target.value))} data-testid={`pres-precio-${i}`} /></div>
            <div className="flex items-end gap-2">
              <div className="flex-1 text-right font-semibold tabular-nums text-blue-700">{formatMoney(Number(it.cantidad || 0) * Number(it.precio_unitario || 0))}</div>
              {form.items.length > 1 && <button onClick={() => rmItem(i)} className="text-red-600 text-xs"><Trash2 className="w-4 h-4" /></button>}
            </div>
          </div>
        ))}
        <div className="flex justify-between items-center mt-3">
          <Button variant="outline" size="sm" onClick={addItem} data-testid="pres-add-item"><Plus className="w-3 h-3 mr-1" /> Item</Button>
          <div className="text-right">
            <div className="text-[10px] uppercase text-zinc-500">Total</div>
            <div className="font-manrope font-black text-2xl text-blue-700">{formatMoney(total)}</div>
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <Button onClick={save} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="pres-save">Crear presupuesto</Button>
        </div>
      </Card>

      <Card className="border-zinc-200 rounded-md overflow-hidden">
        <div className="p-4 border-b border-zinc-100 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Presupuestos ({items.length})</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr className="text-left">
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Fecha</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Proveedor</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Items</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold text-right">Total</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Estado</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {items.length === 0 && <tr><td colSpan={6} className="text-center text-zinc-400 py-10">Sin presupuestos.</td></tr>}
              {items.map((p) => (
                <tr key={p.id} className="hover:bg-zinc-50 align-top" data-testid={`pres-row-${p.id}`}>
                  <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{p.fecha}</td>
                  <td className="px-3 py-2 text-xs">{p.proveedor || "—"}</td>
                  <td className="px-3 py-2 text-xs max-w-md">
                    {p.items.map((it, i) => (
                      <div key={i} className="text-[11px]"><b>{it.cantidad}×</b> {it.descripcion} @ {formatMoney(it.precio_unitario)}</div>
                    ))}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-bold text-blue-700">{formatMoney(p.total)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${badgeCls(p.estado)}`}>{p.estado}</span>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap space-x-1">
                    {p.estado === "pendiente" && (
                      <>
                        <button onClick={() => cambiarEstado(p.id, "aprobado")} className="text-emerald-700 text-xs hover:underline inline-flex items-center gap-1" data-testid={`pres-approve-${p.id}`}><CheckCircle2 className="w-3 h-3" /> Aprobar</button>
                        <button onClick={() => cambiarEstado(p.id, "rechazado")} className="text-red-600 text-xs hover:underline inline-flex items-center gap-1" data-testid={`pres-reject-${p.id}`}><XCircle className="w-3 h-3" /> Rechazar</button>
                      </>
                    )}
                    <button onClick={() => del(p.id)} className="text-red-600 text-xs hover:underline"><Trash2 className="w-3 h-3 inline" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ---------- Panel COMPRAS ----------
function ComprasPanel() {
  const [items, setItems] = useState([]);
  const [productos, setProductos] = useState([]);
  const [presupuestos, setPresupuestos] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [foto, setFoto] = useState(null);
  const [form, setForm] = useState({
    presupuesto_id: "", proveedor: "", fecha: todayIso(),
    monto: 0, metodo_pago: "", banco: "", caja_id: "", categoria: "", factura_numero: "",
    items: [], notas: "",
  });

  const load = async () => {
    try {
      const [c, p, pres, b, cj, cat] = await Promise.all([
        api.get("/inventario/compras"),
        api.get("/inventario/productos"),
        api.get("/inventario/presupuestos"),
        api.get("/bancos"),
        api.get("/cajas").catch(() => ({ data: [] })),
        api.get("/categorias-gasto").catch(() => ({ data: [] })),
      ]);
      setItems(c.data); setProductos(p.data);
      setPresupuestos(pres.data.filter((x) => x.estado === "aprobado"));
      setBancos((b.data || []).filter((x) => x.activo !== false));
      setCajas((cj.data || []).filter((x) => x.activo !== false));
      setCategorias(cat.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const onSelectPresupuesto = (pid) => {
    const p = presupuestos.find((x) => x.id === pid);
    if (!p) { setForm({ ...form, presupuesto_id: "" }); return; }
    setForm({
      ...form,
      presupuesto_id: pid,
      proveedor: p.proveedor || "",
      monto: p.total,
      items: p.items.map((it) => ({ producto_id: it.producto_id || "", descripcion: it.descripcion, cantidad: it.cantidad, precio_unitario: it.precio_unitario })),
    });
  };

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) return toast.error("Foto debe pesar < 3 MB");
    const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
    setFoto(b64);
  };

  const save = async () => {
    if (!form.proveedor.trim()) return toast.error("Proveedor requerido");
    if (!form.monto || form.monto <= 0) return toast.error("Monto > 0");
    if (!form.banco) return toast.error("Banco requerido");
    if (!foto) return toast.error("Foto de la factura del proveedor es obligatoria");
    try {
      await api.post("/inventario/compras", { ...form, foto_factura_base64: foto });
      toast.success("Compra registrada + gasto creado");
      setForm({ presupuesto_id: "", proveedor: "", fecha: todayIso(), monto: 0, metodo_pago: "", banco: "", caja_id: "", categoria: "", factura_numero: "", items: [], notas: "" });
      setFoto(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-5 border-zinc-200 rounded-md">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-3 flex items-center gap-2">
          <Plus className="w-3 h-3" /> Registrar compra al proveedor
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
          <div>
            <Label className="text-xs">Presupuesto aprobado (opcional)</Label>
            <Select value={form.presupuesto_id || "__none__"} onValueChange={(v) => onSelectPresupuesto(v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="comp-presupuesto"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Sin presupuesto —</SelectItem>
                {presupuestos.map((p) => <SelectItem key={p.id} value={p.id}>{p.fecha} · {p.proveedor} · {formatMoney(p.total)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Proveedor *</Label><Input value={form.proveedor} onChange={(e) => setForm({ ...form, proveedor: e.target.value })} data-testid="comp-proveedor" /></div>
          <div><Label className="text-xs">Fecha</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="comp-fecha" /></div>
          <div><Label className="text-xs">Monto (Q) *</Label><Input type="number" step="0.01" value={form.monto} onChange={(e) => setForm({ ...form, monto: Number(e.target.value) })} data-testid="comp-monto" /></div>
          <div><Label className="text-xs">N° Factura</Label><Input value={form.factura_numero} onChange={(e) => setForm({ ...form, factura_numero: e.target.value })} data-testid="comp-factura" /></div>
          <div><Label className="text-xs">Método pago</Label><Input value={form.metodo_pago} onChange={(e) => setForm({ ...form, metodo_pago: e.target.value })} placeholder="Efectivo/Transf..." data-testid="comp-metodo" /></div>
          <div>
            <Label className="text-xs">Categoría <span className="text-[9px] text-zinc-400">(si es "Cargo Expreso" carga saldo a Plataforma de Guías)</span></Label>
            <Select value={form.categoria || ""} onValueChange={(v) => setForm({ ...form, categoria: v === "__none__" ? "" : v })}>
              <SelectTrigger data-testid="comp-categoria"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                <SelectItem value="Cargo Expreso">Cargo Expreso</SelectItem>
                {categorias.filter((c) => (c.nombre || "").toLowerCase() !== "cargo expreso").map((c) => (
                  <SelectItem key={c.id || c.nombre} value={c.nombre}>{c.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Cuenta (Banco o Caja) *</Label>
            <Select
              value={form.caja_id ? `caja:${form.caja_id}` : (form.banco ? `banco:${form.banco}` : "")}
              onValueChange={(v) => {
                if (v.startsWith("banco:")) setForm({ ...form, banco: v.slice(6), caja_id: "" });
                else if (v.startsWith("caja:")) setForm({ ...form, caja_id: v.slice(5), banco: "" });
              }}
            >
              <SelectTrigger data-testid="comp-cuenta"><SelectValue placeholder="Banco o Caja" /></SelectTrigger>
              <SelectContent>
                {bancos.length > 0 && <div className="text-[9px] uppercase tracking-widest text-zinc-400 px-2 pt-1.5 pb-0.5 font-semibold">Bancos</div>}
                {bancos.map((b) => <SelectItem key={`b-${b.id}`} value={`banco:${b.nombre}`}>🏦 {b.nombre}</SelectItem>)}
                {cajas.length > 0 && <div className="text-[9px] uppercase tracking-widest text-zinc-400 px-2 pt-1.5 pb-0.5 font-semibold">Cajas</div>}
                {cajas.map((c) => <SelectItem key={`c-${c.id}`} value={`caja:${c.id}`}>💰 {c.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2"><Label className="text-xs">Notas</Label><Input value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} data-testid="comp-notas" /></div>
        </div>
        <div className="mb-3">
          <Label className="text-xs">Foto factura proveedor * <span className="text-red-600">(obligatoria)</span></Label>
          <Input type="file" accept="image/*" onChange={onFileChange} data-testid="comp-foto" />
          {foto && <img src={foto} alt="preview" className="mt-2 max-h-32 rounded border border-zinc-200" />}
        </div>
        {form.items.length > 0 && (
          <div className="mb-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Items del presupuesto</div>
            {form.items.map((it, i) => (
              <div key={i} className="text-[11px] py-0.5"><b>{it.cantidad}×</b> {it.descripcion} @ {formatMoney(it.precio_unitario)} = <b className="text-blue-700">{formatMoney(it.cantidad * it.precio_unitario)}</b></div>
            ))}
          </div>
        )}
        <div className="flex justify-end">
          <Button onClick={save} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="comp-save">Registrar compra</Button>
        </div>
        <p className="text-[10px] text-zinc-500 mt-2 italic">Se creará automáticamente un gasto en el módulo de Gastos como categoría "Compras Inventario". El stock de los productos vinculados se incrementará.</p>
      </Card>

      <Card className="border-zinc-200 rounded-md overflow-hidden">
        <div className="p-4 border-b border-zinc-100 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Facturas / Compras a proveedores ({items.length})</div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Total</div>
            <div className="font-manrope font-black text-2xl text-blue-700 tabular-nums" data-testid="comp-total">
              {formatMoney(items.reduce((s, x) => s + Number(x.monto || 0), 0))}
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr className="text-left">
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Fecha</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Proveedor</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Factura</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Origen</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold text-right">Monto</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Banco</th>
                <th className="px-3 py-2 text-[10px] uppercase tracking-widest font-semibold">Foto</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {items.length === 0 && <tr><td colSpan={7} className="text-center text-zinc-400 py-10">Sin compras.</td></tr>}
              {items.map((c) => (
                <tr key={c.id} className="hover:bg-zinc-50" data-testid={`comp-row-${c.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{c.fecha}</td>
                  <td className="px-3 py-2 text-xs">{c.proveedor}</td>
                  <td className="px-3 py-2 text-xs font-mono">{c.factura_numero || "—"}</td>
                  <td className="px-3 py-2 text-xs">
                    {c.source === "guia_pedido" ? (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-50 text-orange-700 border border-orange-200">Guía · {c.order_numero || "—"}</span>
                    ) : (
                      <span className="text-zinc-400 text-[10px]">Compra manual</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-blue-700">{formatMoney(c.monto)}</td>
                  <td className="px-3 py-2 text-xs">{c.banco || "—"}</td>
                  <td className="px-3 py-2">
                    {c.source === "guia_pedido" ? (
                      <span className="text-zinc-400 text-[10px]">—</span>
                    ) : (
                      <FotoFactura cid={c.id} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function FotoFactura({ cid }) {
  const [show, setShow] = useState(false);
  const [foto, setFoto] = useState("");
  const open = async () => {
    try {
      const { data } = await api.get(`/inventario/compras/${cid}/factura`);
      setFoto(data.foto_factura_base64 || "");
      setShow(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <>
      <button onClick={open} className="text-blue-700 text-xs hover:underline inline-flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Ver</button>
      {show && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShow(false)}>
          <div className="relative bg-white rounded p-3 max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShow(false)} className="absolute top-2 right-2"><X className="w-5 h-5" /></button>
            {foto ? <img src={foto} alt="factura" className="max-w-full" /> : <p className="text-zinc-400">Sin foto</p>}
          </div>
        </div>
      )}
    </>
  );
}
